'use client';

import {Icon} from '@components/@icons';
import {
    useVideoPlayer,
    useVideoPlayerState,
} from '@components/@shared/VideoPlayer';
import {MeinText} from '@components/@typography/MeinText';
import {AnimatePresence, motion, Variants} from 'framer-motion';
import {useTranslations} from 'next-intl';
import {FC, memo, useEffect, useState} from 'react';
import styles from './VideoUnmuteButton.module.css';

const buttonVariants: Variants = {
    hidden: {opacity: 0, width: '2.25rem', borderRadius: '1.125rem'},
    collapsed: {
        opacity: 1,
        width: '2.25rem',
        borderRadius: '1.125rem',
        transition: {
            opacity: {duration: 0.2},
            width: {duration: 0.4},
            borderRadius: {duration: 0.4, delay: 0.2},
        },
    },
    expanded: {
        opacity: 1,
        width: 'auto',
        borderRadius: '0.375rem',
        transition: {
            opacity: {duration: 0.2},
            width: {duration: 0.3, delay: 0.2},
            borderRadius: {duration: 0.4},
        },
    },
    exit: {
        opacity: 0,
        width: '2.25rem',
        borderRadius: '1.125rem',
        transition: {duration: 0.15, opacity: {duration: 0.2}},
    },
};

const textVariants: Variants = {
    hidden: {opacity: 0},
    collapsed: {opacity: 0, transition: {duration: 0.4}},
    expanded: {opacity: 1, transition: {duration: 0.4, delay: 0.2}},
    exit: {opacity: 0, transition: {duration: 0.15}},
};

const VideoUnmuteButton: FC = memo(function VideoUnmuteButton() {
    const t = useTranslations('liveEvents.liveEvent');
    const [isHintVisible, setIsHintVisible] = useState(false);

    const player = useVideoPlayer();
    const isMuted = useVideoPlayerState(s => s.isMuted);
    const isStarted = useVideoPlayerState(s => s.isStarted);

    useEffect(() => {
        if (!isMuted || !isStarted) {
            return;
        }

        setIsHintVisible(true);
        const hintTimeout = setTimeout(() => setIsHintVisible(false), 3000);

        return () => clearTimeout(hintTimeout);
    }, [isMuted, isStarted]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        player.mute(false);
    };

    return (
        <AnimatePresence>
            {isMuted && isStarted && (
                <motion.div
                    role="button"
                    variants={buttonVariants}
                    initial="hidden"
                    animate={isHintVisible ? 'expanded' : 'collapsed'}
                    whileHover="expanded"
                    exit="exit"
                    className={styles.unmuteButton}
                    onClick={handleClick}
                    title={t('unmute')}
                >
                    <Icon
                        name="volume-mute"
                        className={styles.muteIcon}
                        color="var(--color-icon-primary)"
                    />
                    <motion.div
                        variants={textVariants}
                        className={styles.textContainer}
                    >
                        <MeinText className={styles.muteText}>
                            {t('unmute')}
                        </MeinText>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
});

export default VideoUnmuteButton;
